import cn from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import currency from 'currency.js';

/**
 * The bar fixed to the bottom of the Announcements & Gifts page.
 *
 * TWO VARIANTS, because the page has two audiences:
 *
 *   in the order flow  the customer has a package in the cart and a checkout
 *                      ahead of them. Shows what the order stands at and two
 *                      ways on: skip the add-ons, or continue.
 *   visited directly   a campaign link or a site visit. There is no flow to
 *                      continue, so the bar offers the cart and nothing else —
 *                      and only when the cart has something in it, since
 *                      "View cart (0)" is an invitation to an empty room.
 *
 * ⚠️ DISPLAY AND NAVIGATION ONLY. Neither button adds, prices or changes
 * anything: both handlers push the flow's existing next step, the same one the
 * removed "Continue to Cart" bar used. The money on the left is the cart's own
 * `subtotal`, rendered — never recomputed here.
 *
 * ⚠️ BOTH ACTIONS GO TO THE SAME PLACE, on purpose. "Continue to checkout" and
 * "No thanks, skip add-ons" are the same navigation wearing two labels: adding
 * a gift already routes the customer onward by itself, so by the time they are
 * reading this bar the only thing either control can do is move to the next
 * step. They are kept separate because the customer's INTENT differs, and a
 * shopper who wants none of this should not have to press a button labelled as
 * though they are buying.
 */

function ArrowIcon() {
  return (
    <svg
      className="h-[18px] w-[18px]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      aria-hidden="true"
    >
      <path d="M5 12h13M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The shell is identical in both variants, so it is written once: a customer
// switching between them should not see the bar change height or gutter.
function BarShell({ children, productScreenOpen }) {
  return (
    <div
      /* ⚠️ `max-sm:hidden` WHEN A PRODUCT SCREEN IS UP, never a bare `hidden`
         (Bootstrap declares that `!important`, so nothing could un-hide it).
         This bar and ItemBuilder's StickyBuyBar are both `fixed bottom-0`, and
         StickyBuyBar is phone-only and `z-50` to this bar's `z-30`: with both up
         at 390px its opaque 76px bar sat ON TOP of this one's 168px, burying
         "Continue to checkout" and the skip link under it. Above `sm` there is
         no StickyBuyBar, so this stays exactly as it was. */
      className={cn(
        'fixed inset-x-0 bottom-0 z-30 border-0 border-t border-solid border-gift-line bg-white',
        productScreenOpen && 'max-sm:hidden',
      )}
      style={{ boxShadow: '0 -6px 20px rgba(27, 36, 48, 0.06)' }}
    >
      {/* 88px and one row from `sm` up. On a phone it becomes two rows and
          sizes to them: squeezed onto one, the summary truncated to
          "Subtotal…" — a subtotal the customer cannot read is worse than no
          subtotal at all. */}
      <div
        className={'mx-auto flex min-h-[88px] max-w-[1440px] flex-col justify-center gap-3 px-4 py-3 '
          + 'sm:h-[88px] sm:flex-row sm:items-center sm:gap-6 sm:py-0 md:px-10 lg:px-16'}
      >
        {children}
      </div>
    </div>
  );
}

BarShell.propTypes = {
  children: PropTypes.node.isRequired,
  productScreenOpen: PropTypes.bool,
};

BarShell.defaultProps = { productScreenOpen: false };

// "Package + 1 add-on", not "+ 1 add-ons".
export const addonSummary = (count) => (
  count === 1 ? 'Package + 1 add-on' : `Package + ${count} add-ons`
);

export default function CheckoutBar({
  inOrderFlow, addonCount, subtotal, cartCount, onContinue, onSkip, onViewCart, productScreenOpen,
}) {
  if (!inOrderFlow) {
    // Nothing to offer an empty cart, so the bar stays away entirely rather
    // than sitting there taking 88px off the shelf.
    if (!cartCount) return null;

    return (
      <BarShell productScreenOpen={productScreenOpen}>
        <div className="ml-auto">
          <button
            type="button"
            onClick={onViewCart}
            className={'inline-flex h-[52px] items-center justify-center gap-2.5 rounded-gift-pill '
              + 'border-0 bg-gift-accent px-7 text-[15px] font-bold text-white transition '
              + 'hover:bg-gift-accentHover'}
          >
            {`View cart (${cartCount})`}
          </button>
        </div>
      </BarShell>
    );
  }

  return (
    <BarShell productScreenOpen={productScreenOpen}>
      {/* ⚠️ THE SUBTOTAL ALONE (Alex, 2026-09-29: "get rid of wording for
          'package + ___ add ons', just the subtotal is enough").

          It also removes something that read badly on arrival: `addonCount` is
          0 until the customer adds something, so the first thing an arriving
          customer read at the bottom of the page was "Package + 0 add-ons". The
          count was package-scoped while the subtotal beside it is the whole
          cart's, so for a two-child cart the two numbers were counting
          different things anyway. */}
      <div className="flex min-w-0 items-baseline justify-between gap-3 sm:block sm:flex-1">
        <div className="truncate text-[18px] font-normal leading-tight text-gift-ink sm:text-[20px]">
          {`Subtotal ${currency(subtotal).format()}`}
        </div>
      </div>

      {/* PHONE ORDER: the button, then the skip link centred under it. Reversed
          in the DOM order below so the primary action sits first on a phone
          while the skip link still comes first at desktop, where it reads
          left-to-right as "leave, or continue". */}
      <div className={'flex shrink-0 flex-col-reverse items-stretch gap-3 '
        + 'sm:ml-auto sm:flex-row sm:items-center sm:gap-6'}
      >
        {/* A text link, not a second button: leaving must be findable without
            competing with the action the page is here to offer.
            ⚠️ DROPPED BELOW `sm`. Three items do not fit a phone: at 483px the
            summary, this link and the button overlapped and "Subtotal" was cut
            off mid-word. Nothing is lost by dropping it — skip and continue are
            the same navigation (see the note at the top of this file), so the
            phone keeps the one control that does the job.
            `max-sm:hidden`, never bare `hidden`: Bootstrap's
            `.hidden { display: none !important }` could never be un-hidden. */}
        <button
          type="button"
          onClick={onSkip}
          className={'inline-flex items-center border-0 bg-transparent p-0 text-[15px] '
            + 'font-semibold text-gift-inkSoft underline underline-offset-4 transition '
            + 'hover:text-gift-ink max-sm:justify-center'}
        >
          No thanks, skip add-ons
        </button>

        <button
          type="button"
          onClick={onContinue}
          className={'inline-flex h-[52px] w-full items-center justify-center gap-2.5 '
            + 'rounded-gift-pill border-0 bg-gift-accent px-7 text-[15px] font-bold text-white '
            + 'transition hover:bg-gift-accentHover sm:w-auto'}
        >
          Continue to checkout
          <ArrowIcon />
        </button>
      </div>
    </BarShell>
  );
}

CheckoutBar.defaultProps = {
  productScreenOpen: false,
  addonCount: 0,
  subtotal: 0,
  cartCount: 0,
  onContinue: () => {},
  onSkip: () => {},
  onViewCart: () => {},
};

CheckoutBar.propTypes = {
  // True while ItemBuilder is showing. Phone-only concern: StickyBuyBar is up
  // then, and the two bars are both `fixed bottom-0`.
  productScreenOpen: PropTypes.bool,
  // `?optional=` is the order-flow signal: the customer was SENT here by the
  // package they just built, rather than browsing the shop directly.
  inOrderFlow: PropTypes.bool.isRequired,
  // Add-on sheets on the order package these gifts attach to.
  addonCount: PropTypes.number,
  // The cart's own subtotal, as the server computed it. Rendered, never derived.
  subtotal: PropTypes.number,
  // Total items in the cart, for the direct-visit variant.
  cartCount: PropTypes.number,
  onContinue: PropTypes.func,
  onSkip: PropTypes.func,
  onViewCart: PropTypes.func,
};
